import { Injectable } from '@angular/core'
import { Apollo, QueryRef } from 'apollo-angular'
import gql from 'graphql-tag'
import { Observable } from 'rxjs'
import { ApolloQueryResult } from 'apollo-client'

export interface CustomerData {
  customers: {
    entries: Customer[]
  }
}

export interface UserData {
  currentUser: User
}

export interface User {
  id: number
  role: string
  hiddenCustomers: number[]
  account: Account
  canUpdateCustomer: boolean
  canCreateKeyword: boolean
  canUpdateBpsPreference: boolean
}

export interface Account {
  id: number
  accountName: string
  agency: boolean
  fullySetUp: boolean
  createdByAdmin: string
  accountProfile: {
    id: number
    contactName: string
    contactEmail: string
  }
}

export interface Customer {
  id: number
  businessName: string
  displayName: string
  state: string
  activeKeywordsCount: number
  assignedKeywordsCount: number
  activeRequestsCount: number
  planAddOnCode: string
  hasBpsPreference: boolean
  proposedKeywordsCount: number
  multipass: string
  postCreditsCount: number

  // Signup specific fields
  primaryIndustryName: string
  slider1: number
  slider2: number
  slider3: number
  slider4: number
  slider5: number
  website: string
  blog: string
  twitter: string
  audience: string
  voiceAndPersonality: string
  contentExample: string
  otherInfo: string
  plan: {
    id: number
  }
  subscription: Subscription
}

interface Subscription {
  id: number
  state: string
  selectedProducts: {
    id: number
    code: string
  }[]
  nextCreditsIssuedAt: Date
  scheduledToCancelAt: Date
  scheduledToPauseAt: Date
  quantity: number
    plan: {
      id: number
      words: number
  }
}

export const fragments = {
  user: gql`
    fragment CustomersNavUserFragment on User {
      id
      role
      hiddenCustomers
      canUpdateCustomer: can(action: "update", subject: "Customer")
      canCreateKeyword: can(action: "create", subject: "Keyword")
      canUpdateBpsPreference: can(action: "update", subject: "BpsPreference")
    }
  `,
  account: gql`
    fragment CustomersNavAccountFragment on Account {
      id
      accountName
      agency
      fullySetUp
      createdByAdmin
      accountProfile {
        id
        contactName
        contactEmail
      }
    }
  `,
  customerBase: gql`
    fragment CustomersNavCustomerBaseFragment on Customer {
      id
      businessName
      displayName
      state
      activeKeywordsCount
      assignedKeywordsCount
      activeRequestsCount
      planAddOnCode
      hasBpsPreference
      proposedKeywordsCount
      balance
      multipass
      postCreditsCount
      subscription{
        __typename
        id
        state
        ... on LegacySubscription {
          plan {
            id
            words
          }
          planAddOns {
            code
          }
          nextCreditsIssuedAt
          availablePostCredits
          quantity
        }
        ... on MultipassSubscription {
          state
          selectedProducts {
            id
            code
          }
          nextCreditsIssuedAt
          scheduledToCancelAt
          scheduledToPauseAt
          quantity
        }
      }
    }
  `,
  customerSignup: gql`
    fragment CustomersNavCustomerSignupFragment on Customer {
      primaryIndustryName
      slider1
      slider2
      slider3
      slider4
      slider5
      website
      blog
      twitter
      audience
      voiceAndPersonality
      contentExample
      otherInfo

      plan {
        id
      }
    }
  `
}

const userQuery = gql`
  query CustomersNavUserQuery {
    currentUser {
      ...CustomersNavUserFragment
      account {
        ...CustomersNavAccountFragment
      }
    }
  }
  ${fragments.user}
  ${fragments.account}
`

const customerQuery = gql`
  query CustomersNavCustomersQuery($state: [String], $order: String, $perPage: Int, $withSignupFields: Boolean!) {
    customers(state: $state, order: $order, perPage: $perPage) {
      entries {
        ...CustomersNavCustomerBaseFragment
        ...CustomersNavCustomerSignupFragment @include(if: $withSignupFields)
      }
    }
  }
  ${fragments.customerBase}
  ${fragments.customerSignup}
`

const customerQueryDefaultVariables = {
  state: ['active', 'paused', 'pending', 'canceled', 'expired'],
  order: 'customers.id DESC',
  withSignupFields: false,
  perPage: 999
}

@Injectable({providedIn: 'root'})
export class CustomersNavQueries {
  constructor(private apollo: Apollo) {}

  user(): QueryRef<UserData> {
    return this.apollo.watchQuery<UserData>({
      query: userQuery,
      variables: {},
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network'
    })
  }

  customers({withSignupFields}: { withSignupFields: boolean } = { withSignupFields: false }): QueryRef<CustomerData> {
    return this.apollo.watchQuery<CustomerData>({
      query: customerQuery,
      variables: { ...customerQueryDefaultVariables, withSignupFields },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network'
    })
  }

  // Used in deprecated action calls to refetch after api updates
  refetchUser(): Observable<ApolloQueryResult<UserData>> {
    return this.apollo.query<UserData>({
      query: userQuery,
      fetchPolicy: 'network-only',
    })
  }

  refetchCustomers(): Observable<ApolloQueryResult<CustomerData>> {
    return this.apollo.query<CustomerData>({
      query: customerQuery,
      variables: { ...customerQueryDefaultVariables, withSignupFields: true },
      fetchPolicy: 'network-only',
    })
  }
}
